import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getStoredEndorselyReferral } from '@/lib/endorsely';

const USD_TO_NGN_API_URL = 'https://open.er-api.com/v6/latest/USD';

interface PaystackPaymentProps {
  email: string;
  /** Plan price in USD. Converted to NGN only when opening Paystack. */
  amount: number;
  planName: string;
  onSuccess: () => void;
  onBeforeOpen?: () => void;
  buttonText?: string;
}

declare global {
  interface Window {
    PaystackPop: any;
    Paystack: any;
  }
}

export function PaystackPayment({
  email,
  amount,
  planName,
  onSuccess,
  onBeforeOpen,
  buttonText = 'Subscribe Now',
}: PaystackPaymentProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchUsdToNgnRate = async () => {
    const response = await fetch(USD_TO_NGN_API_URL);
    if (!response.ok) {
      throw new Error('Unable to fetch the latest USD to NGN exchange rate.');
    }

    const data = await response.json();
    const rate = Number(data?.rates?.NGN);

    if (!Number.isFinite(rate) || rate <= 0) {
      throw new Error('The latest USD to NGN exchange rate was unavailable.');
    }

    return rate;
  };

  const getPaystackConstructor = () => {
    if (typeof window.PaystackPop === 'function') return window.PaystackPop;
    if (typeof window.Paystack === 'function') return window.Paystack;
    return null;
  };

  const activateSubscription = async (
    reference: string,
    amountNgn: number,
    exchangeRate: number,
  ) => {
    const endorselyReferralId = getStoredEndorselyReferral();

    const { data, error } = await supabase.functions.invoke('update-subscription', {
      body: {
        paystackReference: reference,
        planName,
        amountNgn,
        amountUsd: amount,
        exchangeRate,
        endorselyReferralId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Payment was verified, but your Pro plan could not be activated.');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Payment was verified, but your Pro plan could not be activated.');
    }

    return data;
  };

  const payWithPaystack = (
    accessCode: string,
    reference: string,
    amountNgn: number,
    exchangeRate: number,
  ) => {
    const successCallback = async (response: any) => {
      console.log('Payment success:', response);

      try {
        const confirmedReference = response?.reference || response?.trxref || reference;
        await activateSubscription(confirmedReference, amountNgn, exchangeRate);

        toast({
          title: 'Payment Successful!',
          description: `Your ${planName} plan is now active.`,
        });

        onSuccess();
      } catch (err: any) {
        console.error('Subscription activation error:', err);
        toast({
          title: 'Payment Received',
          description: err.message || 'Your payment went through, but we could not activate Pro automatically. Please contact support with your payment reference.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    const closeCallback = () => {
      console.log('Payment closed');
      toast({
        title: 'Payment Cancelled',
        description: 'You cancelled the payment process.',
        variant: 'destructive',
      });
      setIsLoading(false);
    };

    const errorCallback = (error: any) => {
      console.error('Paystack load error:', error);
      toast({
        title: 'Payment Failed',
        description: error?.message || 'The payment gateway could not load. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    };

    const PaystackConstructor = getPaystackConstructor();
    if (!PaystackConstructor) {
      errorCallback(new Error('Paystack could not be loaded.'));
      return;
    }

    const popup = new PaystackConstructor();
    if (typeof popup.resumeTransaction !== 'function') {
      errorCallback(new Error('This Paystack script does not support secure transaction resume.'));
      return;
    }

    popup.resumeTransaction(accessCode, {
      onSuccess: successCallback,
      onCancel: closeCallback,
      onError: errorCallback,
      onLoad: () => {
        console.log('Paystack transaction loaded', { reference });
      },
    });
  };

  const handlePayment = async () => {
    setIsLoading(true);

    try {
      const exchangeRate = await fetchUsdToNgnRate();
      const amountNgn = Math.round(amount * exchangeRate);
      const endorselyReferralId = getStoredEndorselyReferral();

      const { data, error } = await supabase.functions.invoke('init-payment', {
        body: {
          email,
          amount: amountNgn,
          amountUsd: amount,
          exchangeRate,
          currency: 'NGN',
          planName,
          endorselyReferralId,
        },
      });

      console.log('Edge function response:', data);

      if (error) {
        throw new Error(error.message || 'Unable to initialize payment.');
      }

      if (!data?.success || !data?.accessCode || !data?.reference) {
        throw new Error(data?.error || 'Unable to initialize payment.');
      }

      if (!getPaystackConstructor()) {
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v2/inline.js';
        script.onload = () => {
          console.log('Paystack loaded, opening popup...');
          onBeforeOpen?.();
          payWithPaystack(data.accessCode, data.reference, amountNgn, exchangeRate);
        };
        script.onerror = () => {
          console.error('Failed to load Paystack');
          toast({
            title: 'Payment Failed',
            description: 'Failed to load payment gateway. Please refresh and try again.',
            variant: 'destructive',
          });
          setIsLoading(false);
        };
        document.body.appendChild(script);
      } else {
        onBeforeOpen?.();
        payWithPaystack(data.accessCode, data.reference, amountNgn, exchangeRate);
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Failed',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  return (
    <Button onClick={handlePayment} disabled={isLoading} className="w-full">
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : (
        buttonText
      )}
    </Button>
  );
}
